import {
  CommonHelperFunctions,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IComponent,
  IDesign,
  IDisplayItem,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IProject,
  IPropertyDefinition,
  IScreenDesign,
  ISymbol,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { PropertySdHelper } from "../../../../shared/utility/property-sd-helper";
import KuikaSelectbox, {
  KuikaSelectboxOption,
  KuikaSelectboxOptionGroup
} from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { isActionParameterShownInPanel } from "./action-parameter-visibility";
import { SymbolPicker } from "./symbol-picker";

const Pagination = "Pagination";
const paginationValuePropName = "current";

const qualityOptions: KuikaSelectboxOption[] = [
  { key: "LOW", value: "LOW", label: "Low" },
  { key: "NORMAL", value: "NORMAL", label: "Normal" },
  { key: "MEDIUM", value: "MEDIUM", label: "Medium" },
  { key: "HIGH", value: "HIGH", label: "High" },
  { key: "EXTREME", value: "EXTREME", label: "Extreme" }
];

const orientationOptions: KuikaSelectboxOption[] = [
  { key: "portrait", value: "portrait", label: "Portrait" },
  { key: "landscape", value: "landscape", label: "Landscape" }
];

// KOA-17684: the action can hand back an image instead of a PDF; PDF stays the default.
const outputFormatOptions: KuikaSelectboxOption[] = [
  { key: "pdf", value: "pdf", label: "PDF" },
  { key: "png", value: "png", label: "PNG (image)" },
  { key: "jpeg", value: "jpeg", label: "JPEG (image)" }
];

export interface IComponentToPdFSymbolGroupState {
  selectedDI: IDisplayItem;
}

export interface IComponentToPdFSymbolGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
}

class ComponentToPdfSymbolGroup extends PureSdComponent<
  IComponentToPdFSymbolGroupProps & IWithSDProps & IWithContextProp,
  IComponentToPdFSymbolGroupState
> {
  propertySdHelper: PropertySdHelper;

  constructor(props: IComponentToPdFSymbolGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      selectedDI: undefined as unknown as IDisplayItem
    };
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);
  }

  componentDidMount() {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    let allDi: IDisplayItem[] = [];
    let allMasterDi: IDisplayItem[] = [];

    this.fillAllDisplayItemsRecursive(sd, allDi);
    const initialSelectValue = this.getSelectValue(allDi);
    let initialValue = allDi[initialSelectValue];
    this.setState({ selectedDI: initialValue });
  }

  getScreenDesignContextProviderValues = () => {
    return this.props.sd;
  };

  handleSelectSymbolPickerValue = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const valueType = "String";
    symbol.targetType = valueType;
    parameter.Typename = valueType;
    const params = _.cloneDeep(this.props.parameters);
    const _parameter = parameter;
    _parameter.symbol = symbol;
    const valuesToUpdate = {
      actionParameters: params.filter((actionParameter) => actionParameter.name !== parameter.name)
    } as IActionPanelStatesCanBeUpdatedFromChild;
    valuesToUpdate.actionParameters?.push(_parameter);
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  handleComponentSelect = (value: any, allDi: IDisplayItem[]) => {
    let fixedValue = allDi[value]?.id;
    let diItem = allDi[value]?.item;
    this.setState({ selectedDI: allDi[value] });

    const params = _.cloneDeep(this.props.parameters);
    const _parameter = this.props.parameters.find((element) => element.name === "targetDiName")!;
    if (_parameter === undefined) {
      return;
    }
    if (!_parameter.symbol) _parameter.symbol = {} as any;
    _parameter.symbol.fixedValue = fixedValue;
    _parameter.symbol.category = ESymbolCategory.fixed;
    let valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;

    const valueProps = [] as IDisplayItemProperty[];
    diItem?.properties?.forEach((element) => {
      const propertyDefinition: IPropertyDefinition | undefined = this.getPropertyDefinition(
        diItem,
        element,
        this.props.sd.project
      );

      if (
        propertyDefinition?.isValueProp ||
        (diItem?.name === Pagination && propertyDefinition?.name == paginationValuePropName)
      ) {
        valueProps.push(element);
      }
    });

    valuesToUpdate.actionParameters = valuesToUpdate.actionParameters?.filter((p) => p.name !== _parameter.name);
    valuesToUpdate.actionParameters?.push(_parameter);
    this.props.updateValues(valuesToUpdate);
  };

  handleQualityChange = (value: string): void => {
    let fixedValue = value;

    const params = _.cloneDeep(this.props.parameters);
    const _parameter = params.find((element) => element.name === "quality")!;
    if (_parameter === undefined) {
      return;
    }
    if (!_parameter.symbol) _parameter.symbol = {} as any;
    _parameter.symbol.fixedValue = fixedValue;
    _parameter.symbol.category = ESymbolCategory.fixed;
    let valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate);
  };

  handleOrientationChange = (value: string): void => {
    let fixedValue = value;

    const params = _.cloneDeep(this.props.parameters);
    const _parameter = params.find((element) => element.name === "orientation")!;
    if (_parameter === undefined) {
      return;
    }
    if (!_parameter.symbol) _parameter.symbol = {} as any;
    _parameter.symbol.fixedValue = fixedValue;
    _parameter.symbol.category = ESymbolCategory.fixed;
    let valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate);
  };

  handleFormatChange = (value: any) => {
    let fixedValue = value;

    const params = _.cloneDeep(this.props.parameters);
    const _parameter = params.find((element) => element.name === "format")!;
    if (_parameter === undefined) {
      return;
    }
    if (!_parameter.symbol) _parameter.symbol = {} as any;
    _parameter.symbol.fixedValue = fixedValue;
    _parameter.symbol.category = ESymbolCategory.fixed;
    let valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate);
  };

  getSelectValue = (allDi: IDisplayItem[]) => {
    let selectValue;
    const _parameter = this.props.parameters.find((element) => element.name === "targetDiName")!;
    if (_parameter && _parameter.symbol) {
      const { fixedValue } = _parameter.symbol;
      allDi.forEach((di, i) => {
        if (di.id.toString() === fixedValue.toString()) {
          selectValue = i;
        }
      });
    }
    return selectValue;
  };

  filterDisplayItemsByValueProperty = (allDi: IDisplayItem[]): IDisplayItem[] => {
    const newDiList = [] as IDisplayItem[];
    allDi.forEach((di) => {
      const { item } = di;
      let isAdded = false;
      item.properties.forEach((element) => {
        const propertyDefinition: IPropertyDefinition | undefined = this.getPropertyDefinition(
          item,
          element,
          this.props.sd.project
        );
        if ((propertyDefinition?.isValueProp || di.item.name === Pagination) && isAdded == false) {
          newDiList.push(di);
          isAdded = true;
        }
      });
    });
    return newDiList;
  };

  getPropertyDefinition = (
    diComponent: IDisplayItemComponent,
    diProperty: IDisplayItemProperty,
    project: IProject
  ): IPropertyDefinition | undefined => {
    if (project.componentSetObj && project.componentSetObj.components) {
      const component: IComponent | undefined = CommonHelperFunctions.findComponentById(
        diComponent?.id,
        project.componentSetObj.components
      );
      if (component) {
        const property = component.properties.find((p) => p.name === diProperty.name);
        if (property) {
          const result: IPropertyDefinition = {
            description: property.description,
            hasOptions: property.hasOptions,
            isRequired: property.isRequired,
            isTechnical: property.isTechnical,
            // isEvent: property.isEvent,
            isVisibleInFieldPicker: property.isVisibleInFieldPicker,
            isValueProp: property.isValueProp,
            isOptionsProp: property.isOptionsProp,
            isLanguageOptionsProp: property.isLanguageOptionsProp,
            isEditabilityProp: property.isEditabilityProp,
            isVisibleProp: property.isVisibleProp,
            isAdvanced: property.isAdvanced,
            isIgnoredOnCodeOutput: property.isIgnoredOnCodeOutput,
            isIgnoredOnGeneratedApp: property.isIgnoredOnGeneratedApp,
            isSupportMultiLanguage: property.isSupportMultiLanguage,
            appSupportType: property.appSupportType,
            isDeprecated: property.isDeprecated,
            name: property.name,
            options: property.options,
            tagName: property.tagName,
            type: property.type,
            typeOptions: property.typeOptions,
            defaultValue: property.symbol?.category === ESymbolCategory.fixed ? property.symbol.fixedValue : "",
            isChartOptionsProp: property.isChartOptionsProp,
            isTimelineOptionsProp: property.isTimelineOptionsProp,
            isChatOptionsProp: property.isChatOptionsProp,
            isCalendarOptionsProp: property.isCalendarOptionsProp,
            isMenuOptionsProp: property.isMenuOptionsProp,
            isBreadcrumbOptionsProp: property.isBreadcrumbOptionsProp,
            isRadialChartOptionsProp: property.isRadialChartOptionsProp,
            isRadialChartLevelOptionsProp: property.isRadialChartLevelOptionsProp,
            isMixedChartOptionsProp: property.isMixedChartOptionsProp,
            isMixedChartLineOptionsProp: property.isMixedChartLineOptionsProp,
            isMixedChartPointOptionsProp: property.isMixedChartPointOptionsProp,
            isMixedChartBarOptionsProp: property.isMixedChartBarOptionsProp,
            isKanbanBoardCardOptionsProp: property.isKanbanBoardCardOptionsProp,
            isKanbanBoardLaneOptionsProp: property.isKanbanBoardLaneOptionsProp,
            isNodeTreeNodeOptionsProp: property.isNodeTreeNodeOptionsProp,
            isTreeOptionsProp: property.isTreeOptionsProp,
            inVisibleStyles: property.inVisibleStyles,
            hideFixedValue: property.hideFixedValue
          };
          return result;
        }
      }
    }
  };

  renderSymbolPicker = (): JSX.Element => {
    let filenameParameter = this.props.parameters.find((element) => element.name === "filename");
    if (!filenameParameter) {
      return <></>;
    }
    return (
      <>
        <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
          <ActionParameterTitle
            parameter={{ name: filenameParameter.name, Typename: "String", cardinality: filenameParameter.cardinality }}
          />
        </span>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          symbol={filenameParameter?.symbol}
          valueType={"String"}
          targetCardinality={filenameParameter?.cardinality}
          targetDI={this.state.selectedDI}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
            this.handleSelectSymbolPickerValue(filenameParameter!, symbol, changeReason)
          }
          bindingParameter={filenameParameter}
        ></SymbolPicker>
      </>
    );
  };

  renderComponentList = (): JSX.Element => {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    let allDi: IDisplayItem[] = [];
    let allMasterDi: IDisplayItem[] = [];

    this.fillAllDisplayItemsRecursive(sd, allDi);

    allDi = allDi.filter((di) => di.id.toString() !== SpecialDIIds.rootDI.toString());
    allDi.sort((a, b) => CommonHelperFunctions.compareObjectWithField(a, b, "friendlyName"));

    // A component is picked by its place in this sorted list rather than by its id, and
    // handleComponentSelect reads the picked index back out of the same list.
    const componentGroups: KuikaSelectboxOptionGroup[] =
      allDi.length > 0
        ? [
            {
              label: "Screen",
              options: allDi.map((di: IDisplayItem, index: number): KuikaSelectboxOption => ({
                key: di.id.toString(),
                value: index,
                label: di.friendlyName
              }))
            }
          ]
        : [];

    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Component to Save as Pdf
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            searchable
            size="sm"
            style={{ width: "100%" }}
            value={this.getSelectValue(allDi) ?? null}
            onChange={(val: number): void => {
              this.handleComponentSelect(val, allDi);
            }}
            className="kuika_add_actions_inputs_size"
            groups={componentGroups}
          />
        </div>
      </>
    );
  };

  renderQuality = (): JSX.Element => {
    let qualityParameter = this.props.parameters.find((element) => element.name === "quality");

    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Quality
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            searchable
            size="sm"
            style={{ width: "100%" }}
            value={qualityParameter?.symbol.fixedValue ?? null}
            onChange={this.handleQualityChange}
            className="kuika_add_actions_inputs_size"
            options={qualityOptions}
          />
        </div>
      </>
    );
  };

  renderOrientation = (): JSX.Element => {
    let orientationParameter = this.props.parameters
      ?.filter((parameter: IActionParameter): boolean => isActionParameterShownInPanel(parameter))
      .find((element) => element.name === "orientation");

    if (!orientationParameter) {
      return <></>;
    }

    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Orientation
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            searchable
            size="sm"
            style={{ width: "100%" }}
            value={orientationParameter?.symbol.fixedValue ?? null}
            onChange={this.handleOrientationChange}
            className="kuika_add_actions_inputs_size"
            options={orientationOptions}
          />
        </div>
      </>
    );
  };

  renderFormat = (): JSX.Element => {
    let formatParameter = this.props.parameters.find((element) => element.name === "format");
    if (!formatParameter) {
      return <></>;
    }
    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Format
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            searchable
            size="sm"
            style={{ width: "100%" }}
            value={formatParameter?.symbol?.fixedValue ?? "pdf"}
            onChange={this.handleFormatChange}
            className="kuika_add_actions_inputs_size"
            options={outputFormatOptions}
          />
        </div>
      </>
    );
  };

  getAllDisplayItemsOfDi = (di: IDisplayItem, result: IDisplayItem[]) => {
    if (di.children)
      for (let i = 0; i < di.children.length; i++) {
        result.push(di.children[i]);
        this.getAllDisplayItemsOfDi(di.children[i], result);
      }
  };

  fillAllDisplayItemsRecursive = (sd: IDesign, result: IDisplayItem[]) => {
    if (sd.displayItems)
      for (let i = 0; i < sd.displayItems.length; i++) {
        result.push(sd.displayItems[i]);
        this.getAllDisplayItemsOfDi(sd.displayItems[i], result);
      }
  };

  render() {
    return (
      <div style={{ width: "100%" }}>
        {this.renderComponentList()}
        {this.renderQuality()}
        {this.renderFormat()}
        {this.renderOrientation()}
        {this.renderSymbolPicker()}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const componentToPdfSymbolGroup = withContext<IComponentToPdFSymbolGroupProps>(
  withSD<IComponentToPdFSymbolGroupProps>(ComponentToPdfSymbolGroup)
);
export { componentToPdfSymbolGroup as ComponentToPdfSymbolGroup };
